// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

export interface ResizableSlideOverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  defaultWidth?: number;
  minWidth?: number;
  /** sessionStorage key for persisting the chosen width within the tab. Default 'gl-inspect-panel-width'. */
  storageKey?: string;
}

const DEFAULT_WIDTH = 440;
const DEFAULT_MIN_WIDTH = 360;
const DEFAULT_STORAGE_KEY = 'gl-inspect-panel-width';
const KEYBOARD_STEP = 24;

function readStoredWidth(key: string, minWidth: number, defaultWidth: number): number {
  try {
    if (typeof window === 'undefined') return defaultWidth;
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return defaultWidth;
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) return defaultWidth;
    const max = window.innerWidth * 0.94;
    return Math.min(Math.max(parsed, minWidth), max);
  } catch {
    return defaultWidth;
  }
}

function persistWidth(key: string, width: number): void {
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(key, String(width));
    }
  } catch {
    // sessionStorage may be unavailable (private browsing, quota); silently ignore.
  }
}

function clampWidth(width: number, minWidth: number): number {
  try {
    if (typeof window === 'undefined') return Math.max(width, minWidth);
    const max = window.innerWidth * 0.94;
    return Math.min(Math.max(width, minWidth), max);
  } catch {
    return Math.max(width, minWidth);
  }
}

export function ResizableSlideOver({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
  defaultWidth = DEFAULT_WIDTH,
  minWidth = DEFAULT_MIN_WIDTH,
  storageKey = DEFAULT_STORAGE_KEY,
}: ResizableSlideOverProps): React.ReactElement {
  const [width, setWidth] = React.useState<number>(() =>
    readStoredWidth(storageKey, minWidth, defaultWidth),
  );

  // Re-read stored width on open so we pick up any persisted value.
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (open && !prevOpenRef.current) {
      setWidth(readStoredWidth(storageKey, minWidth, defaultWidth));
    }
    prevOpenRef.current = open;
  }, [open, storageKey, minWidth, defaultWidth]);

  const contentRef = React.useRef<HTMLDivElement>(null);
  const draggingRef = React.useRef(false);
  const startXRef = React.useRef(0);
  const startWidthRef = React.useRef(0);
  const [isDragging, setIsDragging] = React.useState(false);

  const handlePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      draggingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = width;
      setIsDragging(true);
      // Feature-detect before calling — jsdom may not implement setPointerCapture.
      if (typeof e.currentTarget.setPointerCapture === 'function') {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    },
    [width],
  );

  const handlePointerMove = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      // Dragging the left edge left (toward screen center) widens the right panel.
      const next = startWidthRef.current + (startXRef.current - e.clientX);
      setWidth(clampWidth(next, minWidth));
    },
    [minWidth],
  );

  const handlePointerUp = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setIsDragging(false);
      if (typeof e.currentTarget.releasePointerCapture === 'function') {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      const final = clampWidth(startWidthRef.current + (startXRef.current - e.clientX), minWidth);
      setWidth(final);
      persistWidth(storageKey, final);
    },
    [minWidth, storageKey],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setWidth((prev) => {
          const next = clampWidth(prev + KEYBOARD_STEP, minWidth);
          persistWidth(storageKey, next);
          return next;
        });
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setWidth((prev) => {
          const next = clampWidth(prev - KEYBOARD_STEP, minWidth);
          persistWidth(storageKey, next);
          return next;
        });
      }
    },
    [minWidth, storageKey],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetPortal>
        <SheetOverlay />
        {/*
         * We use SheetPrimitive.Content directly (bypassing the SheetContent
         * wrapper) so we can own the close button and avoid a duplicate. The
         * animation classes from sheetVariants are applied manually here.
         */}
        <SheetPrimitive.Content
          ref={contentRef}
          tabIndex={-1}
          style={{ width, maxWidth: '94vw' }}
          // When no description is supplied, opt out of Radix's described-by check
          // explicitly (the title alone is sufficient here) to silence its advisory.
          {...(description == null ? { 'aria-describedby': undefined } : {})}
          // Move initial focus to the panel itself (not the close button) so opening
          // doesn't auto-highlight the X. Keyboard Tab still rings controls normally.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            contentRef.current?.focus();
          }}
          className={cn(
            'focus:outline-none',
            // Slide animation classes mirrored from sheetVariants({ side: 'right' })
            'fixed z-50 gap-4 bg-background shadow-lg transition ease-in-out',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:duration-300 data-[state=open]:duration-500',
            'inset-y-0 right-0 h-full border-l',
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
            // Remove fixed width so inline style wins.
            '!max-w-none w-auto',
            'flex flex-col p-0 overflow-hidden',
            className,
          )}
        >
          {/* Header */}
          <SheetHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
            <div className="flex items-start justify-between gap-4 pr-2">
              <div className="min-w-0">
                <SheetTitle className="text-base font-semibold text-foreground">{title}</SheetTitle>
                {description != null && (
                  <SheetDescription className="mt-1 text-sm text-muted-foreground">
                    {description}
                  </SheetDescription>
                )}
              </div>
              <SheetClose
                aria-label="Close"
                // Gray hover fill (no fill at rest); focus ring only for keyboard
                // (focus-visible), so opening via a mouse click doesn't flash a ring.
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </SheetClose>
            </div>
          </SheetHeader>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">{children}</div>

          {/* Optional sticky footer */}
          {footer != null && (
            <div className="shrink-0 border-t border-border px-6 py-4">{footer}</div>
          )}

          {/*
           * Resize handle — a subtle macOS-style grabber on the left edge. The hit
           * zone spans the full height for easy grabbing (col-resize cursor signals
           * draggability); the small centered pill is neutral at rest and only
           * darkens on hover, keyboard focus, or while dragging. Rendered last so it
           * isn't the panel's initial focus target when the drawer opens.
           */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
            tabIndex={0}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onKeyDown={handleKeyDown}
            className="group/resize absolute inset-y-0 left-0 z-10 flex w-2.5 cursor-col-resize items-center justify-center focus-visible:outline-none"
          >
            <span
              aria-hidden="true"
              className={cn(
                'h-8 w-1 rounded-full transition-colors',
                isDragging
                  ? 'bg-slate/60'
                  : 'bg-border group-hover/resize:bg-slate/40 group-focus-visible/resize:bg-slate/40',
              )}
            />
          </div>
        </SheetPrimitive.Content>
      </SheetPortal>
    </Sheet>
  );
}
